import { T } from '@transifex/react';
import { Alert, AlertText, cn, Text, VStack } from '@vidahealth/vida-ui';
import { deviceType, DeviceType } from 'expo-device';
import { Linking, Platform, Text as RNText } from 'react-native';

/** Canonical support address — the single source of truth for `support@vida.com`. */
export const DEFAULT_EMAIL = 'support@vida.com';
const DEFAULT_PHONE = '(833) 670-5552';

type Size = 'sm' | 'md';

type ContactSupportProps = {
  /** Defaults to `support@vida.com`. Pass `null` to omit the email scenario. */
  email?: string | null;
  /** Defaults to `(833) 670-5552`. Pass `null` to omit the phone scenario. */
  phone?: string | null;
  /** Defaults to `false`. Pass `true` to show the email scenario. */
  showEmail?: boolean;
  /** Defaults to `false`. Pass `true` to show the phone scenario. */
  showPhone?: boolean;
  /**
   * Defaults to `'inline'` — a single sentence ("Want help? Call us at …").
   * `'alert'` renders an info-styled Alert with a bold heading and the
   * enabled contact channels on separate lines.
   */
  variant?: 'inline' | 'alert';
  className?: string;
};

/**
 * Onboarding's "talk to support" affordance. Two visual modes share one
 * component because both deal with the same email + phone primitives:
 *
 *  - `inline` (default) — single-sentence variants mirroring stylos's
 *    `ContactSupportCard` scenarios:
 *      both    → "Want help? Please contact support at {email} or call {phone}."
 *      email   → "Want help? Please contact support at {email}."
 *      phone   → "Want help? Call us at {phone}."
 *      neither → "Want help? Please contact support."
 *
 *  - `alert` — info-style alert with a bold "Our support team is here to
 *    help:" heading and Call / Email lines below it. Used as a fallback on
 *    "we couldn't find you" screens where there's no org-specific copy.
 *
 * In both modes: email is always a `mailto:` link; phone is a tappable
 * `tel:` link on native and on mobile web (phones/tablets), where dialing
 * works — on desktop web, where `tel:` dead-ends, the number renders as plain
 * text. See `canTapToCall`.
 */
export function ContactSupport({
  email = DEFAULT_EMAIL,
  phone = DEFAULT_PHONE,
  showEmail = false,
  showPhone = false,
  variant = 'inline',
  className,
}: ContactSupportProps) {
  if (variant === 'alert') {
    return (
      <Alert action='info' variant='solid' className={cn('flex-col items-start p-6', className)}>
        <VStack space='xs'>
          <AlertText className='font-inter-600' size='md'>
            <T _str='Our support team is here to help:' />
          </AlertText>
          {phone && showPhone ? (
            <AlertText size='sm'>
              <T _str='Call {phone}' phone={<PhoneLink phone={phone} size='sm' />} />
            </AlertText>
          ) : null}
          {email && showEmail ? (
            <AlertText size='sm'>
              <T _str='Email {email}.' email={<EmailLink email={email} size='sm' />} />
            </AlertText>
          ) : null}
        </VStack>
      </Alert>
    );
  }

  const prefix = (
    <Text className='text-typography-900'>
      <T _str='Want help?' />
    </Text>
  );

  if (email && phone && showEmail && showPhone) {
    return (
      <Text size='md' className={cn('text-typography-900', className)}>
        <T
          _str='{prefix} Please contact support at {email} or call {phone}.'
          prefix={prefix}
          email={<EmailLink email={email} size='md' />}
          phone={<PhoneLink phone={phone} size='md' />}
        />
      </Text>
    );
  }

  if (email && showEmail) {
    return (
      <Text size='md' className={cn('text-typography-900', className)}>
        <T
          _str='{prefix} Please contact support at {email}.'
          prefix={prefix}
          email={<EmailLink email={email} size='md' />}
        />
      </Text>
    );
  }

  if (phone && showPhone) {
    return (
      <Text size='md' className={cn('text-typography-900', className)}>
        <T
          _str='{prefix} Call us at {phone}.'
          prefix={prefix}
          phone={<PhoneLink phone={phone} size='md' />}
        />
      </Text>
    );
  }

  return (
    <Text size='md' className={cn('text-typography-900', className)}>
      <T _str='{prefix} Please contact support.' prefix={prefix} />
    </Text>
  );
}

// vida-ui's `size` prop → font-size class, for the bare RN Text links below.
const SIZE_CLASS: Record<Size, string> = { sm: 'text-sm', md: 'text-base' };

// Inline tappable links use a bare RN Text + onPress, NOT vida-ui's Pressable
// `Link`: a Pressable nested in a Text run doesn't share the text baseline on
// native, so it renders vertically misaligned against the surrounding copy. A
// Text node stays inline. (Same reason LegalDocumentCheckbox's PolicyLink uses
// a bare RN Text.) `.catch` swallows the openURL rejection — e.g. the iOS
// Simulator has no Phone app, so `tel:` rejects there (works on device).
function EmailLink({ email, size }: { email: string; size: Size }) {
  return (
    <RNText
      onPress={() => void Linking.openURL(`mailto:${email}`).catch(() => {})}
      className={`${SIZE_CLASS[size]} text-info-700`}
      role='link'
    >
      {email}
    </RNText>
  );
}

// Whether tapping the number to dial is meaningful. Native always dials, so
// it's left unconditional (gating native on deviceType would risk a regression
// where expo-device reports UNKNOWN). On web, only phones/tablets can place a
// call from a `tel:` link, so desktop keeps plain text to avoid a dead link.
// expo-device derives deviceType from the user agent on web.
function canTapToCall(): boolean {
  if (Platform.OS !== 'web') return true;
  return deviceType === DeviceType.PHONE || deviceType === DeviceType.TABLET;
}

function PhoneLink({ phone, size }: { phone: string; size: Size }) {
  if (!canTapToCall()) {
    return (
      <Text size={size} className='text-primary-500'>
        {phone}
      </Text>
    );
  }
  const digits = phone.replace(/\D/g, '');
  return (
    <RNText
      onPress={() => void Linking.openURL(`tel:${digits}`).catch(() => {})}
      className={`${SIZE_CLASS[size]} text-info-700`}
      role='link'
    >
      {phone}
    </RNText>
  );
}
